import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getLexique } from "../../../../services/lexiqueServices";
import { getLexiqueKarate } from "../../../../services/lexiquekarateServices";
import { getSettings } from "../../../../services/settingsServices";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_LEXIQUE_FILTERS,
  buildLetterOptions,
  countActiveLexiqueFilters,
  filterLexiqueEntries,
  getLexiqueGroups,
  getLexiqueStats,
  normalizeDiscipline,
  sortLexiqueEntries,
} from "../utils/lexiqueUtils";

export function useLexiquePage() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [lexique, setLexique] = useState([]);
  const [discipline, setDiscipline] = useState("Judo");
  const [filters, setFilters] = useState(DEFAULT_LEXIQUE_FILTERS);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const fetchLexique = useCallback(async ({ silent = false } = {}) => {
    const isInitialLoad = !hasLoadedOnceRef.current;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const settings = await getSettings();
      const disciplineName =
        settings?.[0]?.discipline_nom ||
        settings?.discipline_nom ||
        "Judo";

      const normalizedDiscipline = normalizeDiscipline(disciplineName);

      setDiscipline(normalizedDiscipline);

      const response =
        normalizedDiscipline === "Karate"
          ? await getLexiqueKarate()
          : await getLexique();

      const safeLexique = Array.isArray(response) ? response : [];

      setLexique(sortLexiqueEntries(safeLexique));

      if (!silent && !isInitialLoad) {
        notificationRef.current?.("Lexique actualisé.", "success");
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du lexique :", error);

      notificationRef.current?.(
        "Erreur lors de la récupération du lexique.",
        "error",
      );

      setLexique([]);
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchLexique({ silent: true });
  }, [fetchLexique]);

  const filteredLexique = useMemo(() => {
    return filterLexiqueEntries(lexique, filters);
  }, [lexique, filters]);

  const groupedLexique = useMemo(() => {
    return getLexiqueGroups(filteredLexique);
  }, [filteredLexique]);

  const availableLetters = useMemo(() => {
    return buildLetterOptions(lexique);
  }, [lexique]);

  const stats = useMemo(() => {
    return getLexiqueStats(lexique);
  }, [lexique]);

  const activeFiltersCount = useMemo(() => {
    return countActiveLexiqueFilters(filters);
  }, [filters]);

  const handleFilterChange = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_LEXIQUE_FILTERS);
  }, []);

  return {
    lexique,
    filteredLexique,
    groupedLexique,
    discipline,
    filters,
    availableLetters,
    stats,
    loading,
    refreshing,
    activeFiltersCount,
    fetchLexique,
    handleFilterChange,
    resetFilters,
  };
}